import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { getTitleComponentId } from '@shared/data/entity';
import { useDefaultFields } from '@shared/scenes/entity/create/hooks/useDefaultFields';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isInputComponent, isTextComponent } from '@shared/scenes/workflows/utils';

export function useWorkflowSupportsQuickCreate({
  workflowTemplateId,
}: {
  workflowTemplateId?: string | null;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId ?? undefined);
  const { components } = workflowTemplate ?? {};
  const defaultFields = useDefaultFields({ workflowTemplateId, isViewTemplateRequired: false });

  return useMemo(() => {
    /* Quick create seeds exactly one field — the related entity's title — so it is
     * only offered when that's enough to make a valid card:
     *  - The title component isn't guaranteed, and quick create can only seed the
     *    typed text, so it must exist and be a text component, for now.
     *  - Any other required field without a default would leave the card invalid,
     *    since quick create has no way to set it.
     */
    const titleComponentId = getTitleComponentId(workflowTemplate);
    const titleComponent = components?.find(c => c.id === titleComponentId);
    const relatedWorkflowHasTitle = !isNil(titleComponent) && isTextComponent(titleComponent);

    const relatedWorkflowHasOtherRequiredFields = components
      ?.filter(isInputComponent)
      .filter(c => c.id !== titleComponentId)
      .some(c => {
        const hasDefault = !isEmpty(defaultFields?.[c.id]);
        const isRequired = c.required;

        return isRequired && !hasDefault;
      });
    return relatedWorkflowHasTitle && !relatedWorkflowHasOtherRequiredFields;
  }, [workflowTemplate, components, defaultFields]);
}
